import { MASTODON_TAB_PREFS_KEY_PREFIX } from '@/constants/storage'
import type { TabEntry } from '@/services/userPreferencesService'
import type { ResolvedFeed } from '@/types/feeds'

/** Sentinel id for the locked Following tab (always visible, always position 0). */
export const FOLLOWING_TAB_ID = 'following'

/** The structured Mastodon tab preference stored per account under localStorage. */
export interface MastodonTabsPreference {
    version: 1
    tabs: TabEntry[]
}

/** Build the account-scoped localStorage key for a Mastodon account's tab preferences. */
export function mastodonTabPrefsKey(scope: string): string {
    return `${MASTODON_TAB_PREFS_KEY_PREFIX}:${scope}`
}

/**
 * Hardened parse of a raw localStorage value into a MastodonTabsPreference.
 * Returns null for anything malformed (wrong version, non-object JSON, arrays, null,
 * duplicate/missing ids, non-boolean visible, non-finite/negative position) — callers
 * should treat null as "no stored preference" and fall back to defaults. Unknown keys
 * (e.g. a leftover defaultTabId from an older record) are ignored rather than rejected.
 */
export function parseMastodonTabPrefs(raw: string | null): MastodonTabsPreference | null {
    if (raw === null) return null

    let parsed: unknown
    try {
        parsed = JSON.parse(raw)
    } catch {
        return null
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
    const obj = parsed as Record<string, unknown>

    if (obj.version !== 1) return null
    if (!Array.isArray(obj.tabs)) return null

    const seenIds = new Set<string>()
    const tabs: TabEntry[] = []
    for (const entry of obj.tabs) {
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return null
        const e = entry as Record<string, unknown>
        if (typeof e.id !== 'string' || e.id.length === 0) return null
        if (seenIds.has(e.id)) return null
        if (typeof e.visible !== 'boolean') return null
        if (typeof e.position !== 'number' || !Number.isFinite(e.position) || e.position < 0) {
            return null
        }
        seenIds.add(e.id)
        tabs.push({ id: e.id, visible: e.visible, position: e.position })
    }

    return { version: 1, tabs }
}

/**
 * Write a Mastodon tab preference record and broadcast a synthetic `storage` event so
 * every other mounted `useMastodonTabPrefs` instance for this key re-reads immediately
 * (native `storage` events only fire cross-tab). May throw (quota, privacy mode) — callers
 * catch.
 */
export function writeMastodonTabPrefs(storageKey: string, next: MastodonTabsPreference): void {
    const serialized = JSON.stringify(next)
    localStorage.setItem(storageKey, serialized)
    window.dispatchEvent(
        new StorageEvent('storage', {
            key: storageKey,
            newValue: serialized,
            storageArea: localStorage,
        })
    )
}

/**
 * Unfollow tombstone clear (plan decision 2 for mackinac#2355): removes a single stored
 * tab entry for the given account scope, so that a later refollow of the same hashtag is
 * never suppressed by a stale hidden/ordered entry — it comes back visible at the end via
 * `reconcileMastodonTabs` rule 2. Takes `scope` as a parameter (rather than reading the
 * currently active session) so a caller that captured its account at invocation time pins
 * the write to that account regardless of which account is active when the write actually
 * runs. Never prunes via a `liveFeeds` reconcile — positions simply renormalize on next
 * read. Returns `true` on success or "nothing to clear" (no throw), `false` on a write
 * failure — never throws, since this is best-effort bookkeeping.
 */
export function removeStoredMastodonTab(scope: string, tabId: string): boolean {
    if (tabId === FOLLOWING_TAB_ID) return false
    const storageKey = mastodonTabPrefsKey(scope)
    try {
        const current = parseMastodonTabPrefs(localStorage.getItem(storageKey))
        if (current === null) return true
        if (!current.tabs.some(t => t.id === tabId)) return true
        writeMastodonTabPrefs(storageKey, {
            version: 1,
            tabs: current.tabs.filter(t => t.id !== tabId),
        })
        return true
    } catch {
        return false
    }
}

/**
 * Reconcile stored tab entries against the current authoritative set of live feeds
 * (base tabs + lists + followed hashtags, per useSavedFeeds' Mastodon branch).
 *
 * Rules:
 * 1. Empty stored → derive default order [Following, ...liveFeeds] all visible.
 * 2. Stored order wins; live items missing from stored are appended, visible=true.
 * 3. Stored entries for live items that no longer exist (e.g. deleted list) are pruned.
 * 4. Following is always present, always visible, always re-anchored to position 0 —
 *    it is the guaranteed fallback target for this feature's guards (D8).
 * 5. Positions are re-normalized to 0-based sequential.
 */
export function reconcileMastodonTabs(stored: TabEntry[], liveFeeds: ResolvedFeed[]): TabEntry[] {
    const liveIds = new Set(liveFeeds.map(f => f.uri))

    if (stored.length === 0) {
        let pos = 0
        const result: TabEntry[] = [{ id: FOLLOWING_TAB_ID, visible: true, position: pos++ }]
        for (const feed of liveFeeds) {
            if (feed.uri === FOLLOWING_TAB_ID) continue
            result.push({ id: feed.uri, visible: true, position: pos++ })
        }
        return result
    }

    // Keep stored entries that are still live (Following always kept — re-anchored below).
    const kept = stored.filter(t => t.id === FOLLOWING_TAB_ID || liveIds.has(t.id))
    const keptIds = new Set(kept.map(t => t.id))

    // Append any live items not already tracked, visible=true.
    const toAdd: TabEntry[] = []
    for (const feed of liveFeeds) {
        if (feed.uri === FOLLOWING_TAB_ID) continue
        if (!keptIds.has(feed.uri)) {
            toAdd.push({ id: feed.uri, visible: true, position: 0 })
        }
    }

    const withoutFollowing = [...kept, ...toAdd].filter(t => t.id !== FOLLOWING_TAB_ID)
    const merged = [{ id: FOLLOWING_TAB_ID, visible: true, position: 0 }, ...withoutFollowing]

    return merged.map((t, i) => ({ ...t, position: i }))
}
